import React, { useContext, useState } from 'react';
import { DiscountBadge, DiscountBadgeVariant, UserContext } from '@secretescapes/cauldron';
import {
  Gallery as FalconGallery,
  GalleryElement,
  Lightbox,
  useTheme,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useTranslations } from '@utils/useTranslations';
import { InteractionButtons } from '@features/Sale/InteractionButtons';
import { GalleryOverlay, StyledDiscountBadge } from './Gallery.styles';
import { Modal } from './Gallery.utils';
import { GalleryAction } from './GalleryAction';
import { GalleryModal } from './Modal';
import { useGalleryNavigation } from './useGalleryNavigation';

const THUMBS_COUNT = 4;

interface Props {
  images: GalleryElement[];
  saleId: string;
  saleTitle: string;
  isFavourite: boolean;
  isMobile: boolean;
  showUrgentDiscount: boolean;
  discount: string;
  discountTooltip?: string;
  refetchFavourites: () => void;
}

export const Gallery = ({
  images,
  saleId,
  saleTitle,
  isFavourite,
  isMobile,
  showUrgentDiscount,
  discount,
  discountTooltip,
  refetchFavourites,
}: Props) => {
  const theme = useTheme();
  const { userId } = useContext(UserContext);
  const { translations } = useTranslations();
  const [focusedLightboxImage, setFocusedLightboxImage] = useState(0);
  const [focusedImageIndex, setFocusedImageIndex] = useState<number | undefined>();
  const { closeModal, openModal, showLightbox, showGalleryModal } = useGalleryNavigation();

  const handleOpenModal = (imageIndex?: number) => {
    if (userId) {
      refetchFavourites();
    }

    openModal(Modal.gallery);
    setFocusedImageIndex(imageIndex);
  };

  const handleImageClick = (imageIndex: number) => {
    openModal(Modal.lightbox);
    setFocusedLightboxImage(imageIndex);
  };

  const handleCloseModal = () => {
    if (userId) {
      refetchFavourites();
    }

    closeModal(Modal.gallery);
    setFocusedImageIndex(undefined);
  };

  return (
    <>
      <FalconGallery
        elements={images}
        thumbsCount={THUMBS_COUNT}
        onImageClick={handleOpenModal}
        isMobileIntrinsicSize={isMobile}
      >
        <>
          <GalleryOverlay isMobile={isMobile}>
            {isMobile && (
              <InteractionButtons
                saleId={saleId}
                saleTitle={saleTitle}
                isFavourite={!!isFavourite}
                isMobile={isMobile}
              />
            )}
            {showUrgentDiscount ? (
              <StyledDiscountBadge>
                <DiscountBadge
                  discountPrice={discount}
                  variant={DiscountBadgeVariant.urgent}
                  discountTooltip={discountTooltip}
                  showTooltip={!!discountTooltip}
                  data-testid="discount-badge"
                  theme={theme}
                />
              </StyledDiscountBadge>
            ) : null}
          </GalleryOverlay>
          <GalleryAction onClick={handleOpenModal} totalImages={images.length} />
        </>
      </FalconGallery>

      <GalleryModal
        isMobile={isMobile}
        modalOpen={showGalleryModal}
        onClose={handleCloseModal}
        saleId={saleId}
        saleTitle={saleTitle}
        images={images}
        isFavourite={!!isFavourite}
        focusedImageIndex={focusedImageIndex}
        onImageClick={handleImageClick}
      />

      {showLightbox && (
        <Lightbox
          dataAttributes={{ testid: 'gallery-lightbox' }}
          closeButtonText={i18n('common.close.label', [], translations)}
          images={images}
          infiniteCarousel={true}
          initialSlide={focusedLightboxImage}
          onClose={() => closeModal(Modal.lightbox)}
          showNavigation={true}
          animation={true}
        />
      )}
    </>
  );
};
